import { Minus, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useShippingSettings } from '@/hooks/useShippingSettings';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FreeShippingProgress } from '@/components/FreeShippingProgress';

interface CartSidebarProps {
  open: boolean;
  onClose: () => void;
}

export const CartSidebar = ({ open, onClose }: CartSidebarProps) => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeFromCart, total } = useCart();
  const { t, language } = useLanguage();
  const { calculateShipping } = useShippingSettings();

  const shipping = calculateShipping(total);
  const grandTotal = total + shipping;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side={language === 'ar' ? 'left' : 'right'} className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-serif text-2xl">{t('cart')}</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground">
            <p className="text-lg">{t('cartEmpty')}</p>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
              <div className="space-y-4 py-6">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 bg-card rounded-lg shadow-soft"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{item.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {item.price} SEK
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ml-auto text-destructive"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex-shrink-0 border-t pt-4 space-y-4 pb-4">
              {/* Free Shipping Progress Bar */}
              <FreeShippingProgress subtotal={total} />
              
              {/* Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('cartSubtotal')}:</span>
                  <span>{total.toFixed(2)} SEK</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('cartShipping')}:</span>
                  <span>{shipping === 0 ? t('cartFree') : `${shipping.toFixed(2)} SEK`}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                  <span>{t('cartTotal')}:</span>
                  <span>{grandTotal.toFixed(2)} SEK</span>
                </div>
              </div>
              <Button 
                className="w-full btn-primary"
                onClick={() => {
                  onClose();
                  navigate('/checkout');
                }}
              >
                {t('proceedToCheckout')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};